import { useState, useEffect } from 'react';
import Grid from '@mui/material/Grid';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import Tooltip from '@mui/material/Tooltip';
import IconButton from '@mui/material/IconButton';
import Chip from '@mui/material/Chip';
import { useReduxProject } from '@Hooks/reduxProjectHook/useReduxProject';
import ConfirmationNumberOutlinedIcon from '@mui/icons-material/ConfirmationNumberOutlined';
import PlayCircleOutlineIcon from '@mui/icons-material/PlayCircleOutline';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import '@Partials/_ComStyles.scss';
import '@Partials/_SimpleStyles.scss';
import '@Partials/_OverrideStyles.scss';
import './AnalyticsCharts.scss';

export default function AnalyticsCharts(props) {
    const [activeCount, setActiveCount] = useState(0);
    const [resolvedPercentage, setResolvedPercentage] = useState(0);
    const [activePercentage, setActivePercentage] = useState(0);
    const { currentUserProjectPermissions } = useReduxProject();
    const className = 'ticketDashboards__analyticsCharts';

    useEffect(() => {
        if (!props.ticketStatusList || !Array.isArray(props.ticketStatusList)) return;
        
        const activeStatuses = ['reported', 'open', 'reopened', 'escalated'];
        const activeTicketCount = props.ticketStatusList.reduce((active, ticketStatusObj) => {
            const statusValue = ticketStatusObj?.status?.value;
            const count = ticketStatusObj?.count || 0;
            if (activeStatuses.includes(statusValue)) {
                return active + count;
            }
            return active;
        }, 0);
        
        setActiveCount(activeTicketCount);
        
        const totalTickets = props.kaiziqResolveConnectionAnalytics?.totalTickets || 0;
        const resolvedTickets = props.kaiziqResolveConnectionAnalytics?.resolvedTickets || 0;
        
        if (totalTickets > 0) {
            setResolvedPercentage(((resolvedTickets / totalTickets) * 100).toFixed(1));
            setActivePercentage(((activeTicketCount / totalTickets) * 100).toFixed(1));
        }
    }, [props.ticketStatusList, props.kaiziqResolveConnectionAnalytics]);

    const getStatusIcon = (statusValue) => {
        switch (statusValue) {
            case 'reported':
            case 'open':
                return <ErrorOutlineIcon />;
            case 'resolved':
            case 'closed':
                return <CheckCircleOutlineIcon />;
            case 'reopened':
            case 'escalated':
                return <PlayCircleOutlineIcon />;
            default:
                return <ConfirmationNumberOutlinedIcon />;
        }
    };

    const renderPrimaryStats = () => (
        <Box className={className + '__primaryStats'}>
            <Grid container spacing={2}>
                {/* Total Tickets */}
                <Grid item xs={12} sm={6} md={4}>
                    <Paper
                        className={`${className}__statCard ${className}__statCard--primary`}
                        elevation={0}
                        onClick={(e) => props.handleFormTabPositionClick(e, 1)}
                    >
                        <Box className={className + '__statCard__header'}>
                            <Box className={className + '__statCard__icon statCard__icon--primary'}>
                                <ConfirmationNumberOutlinedIcon />
                            </Box>
                            <Tooltip title="View all tickets">
                                <IconButton size="small" className={className + '__statCard__action'}>
                                    <InfoOutlinedIcon fontSize="small" />
                                </IconButton>
                            </Tooltip>
                        </Box>
                        <Box className={className + '__statCard__content'}>
                            <Typography className={className + '__statCard__label'}>
                                Total Tickets
                            </Typography>
                            <Typography className={className + '__statCard__value'}>
                                {props.kaiziqResolveConnectionAnalytics?.totalTickets || 0}
                            </Typography>
                        </Box>
                        <Box className={className + '__statCard__footer'}>
                            <Chip
                                label="All time"
                                size="small"
                                className={className + '__statCard__chip'}
                            />
                        </Box>
                    </Paper>
                </Grid>

                {/* Active Tickets */}
                <Grid item xs={12} sm={6} md={4}>
                    <Paper
                        className={`${className}__statCard ${className}__statCard--warning`}
                        elevation={0}
                        onClick={(e) =>
                            props.applyFilterForTicketsPage([
                                props.ticketStatusList[0],
                                props.ticketStatusList[1],
                                props.ticketStatusList[3],
                                props.ticketStatusList[4]
                            ])
                        }
                    >
                        <Box className={className + '__statCard__header'}>
                            <Box className={className + '__statCard__icon statCard__icon--warning'}>
                                <PlayCircleOutlineIcon />
                            </Box>
                            <Box className={className + '__statCard__trend'}>
                                {activePercentage > 50 ? (
                                    <TrendingUpIcon className="trend--up" />
                                ) : (
                                    <TrendingDownIcon className="trend--down" />
                                )}
                                <Typography className={className + '__statCard__percentage'}>
                                    {activePercentage}%
                                </Typography>
                            </Box>
                        </Box>
                        <Box className={className + '__statCard__content'}>
                            <Typography className={className + '__statCard__label'}>
                                Active Tickets
                            </Typography>
                            <Typography className={className + '__statCard__value'}>
                                {activeCount}
                            </Typography>
                        </Box>
                        <Box className={className + '__statCard__footer'}>
                            <Chip
                                label="Needs attention"
                                size="small"
                                className={`${className}__statCard__chip chip--warning`}
                            />
                        </Box>
                    </Paper>
                </Grid>

                {/* Resolved Tickets */}
                <Grid item xs={12} sm={12} md={4}>
                    <Paper
                        className={`${className}__statCard ${className}__statCard--success`}
                        elevation={0}
                        onClick={(e) =>
                            props.ticketStatusList?.[2] &&
                            props.applyFilterForTicketsPage([props.ticketStatusList[2]])
                        }
                    >
                        <Box className={className + '__statCard__header'}>
                            <Box className={className + '__statCard__icon statCard__icon--success'}>
                                <CheckCircleOutlineIcon />
                            </Box>
                            <Box className={className + '__statCard__trend'}>
                                {resolvedPercentage > 50 ? (
                                    <TrendingUpIcon className="trend--up" />
                                ) : (
                                    <TrendingDownIcon className="trend--down" />
                                )}
                                <Typography className={className + '__statCard__percentage'}>
                                    {resolvedPercentage}%
                                </Typography>
                            </Box>
                        </Box>
                        <Box className={className + '__statCard__content'}>
                            <Typography className={className + '__statCard__label'}>
                                Resolved Tickets
                            </Typography>
                            <Typography className={className + '__statCard__value'}>
                                {props.kaiziqResolveConnectionAnalytics?.resolvedTickets || 0}
                            </Typography>
                        </Box>
                        <Box className={className + '__statCard__footer'}>
                            <Chip
                                label="Completed"
                                size="small"
                                className={`${className}__statCard__chip chip--success`}
                            />
                        </Box>
                    </Paper>
                </Grid>
            </Grid>
        </Box>
    );

    const renderStatusBreakdown = () => (
        <Box className={className + '__statusBreakdown'}>
            <Typography className={className + '__statusBreakdown__title'} variant="h6">
                Status Breakdown
            </Typography>
            <Grid container spacing={2}>
                {props?.ticketStatusList?.map((ticketStatusObj, index) => (
                    <Grid item xs={6} sm={4} md={2.4} key={index}>
                        <Paper
                            className={className + '__statusCard'}
                            elevation={0}
                            onClick={(e) => props.applyFilterForTicketsPage([ticketStatusObj])}
                            sx={{
                                '--status-color': ticketStatusObj?.status?.color || '#5f6368',
                                '--status-color-light': ticketStatusObj?.status?.color
                                    ? `${ticketStatusObj.status.color}20`
                                    : '#f8f9fa'
                            }}
                        >
                            <Box className={className + '__statusCard__header'}>
                                <Box
                                    className={className + '__statusCard__icon'}
                                    sx={{ color: ticketStatusObj?.status?.color }}
                                >
                                    {getStatusIcon(ticketStatusObj?.status?.value)}
                                </Box>
                            </Box>
                            <Box className={className + '__statusCard__content'}>
                                <Typography
                                    className={className + '__statusCard__value'}
                                    sx={{ color: ticketStatusObj?.status?.color }}
                                >
                                    {ticketStatusObj.count}
                                </Typography>
                                <Typography className={className + '__statusCard__label'}>
                                    {ticketStatusObj?.status?.title}
                                </Typography>
                            </Box>
                            <Box
                                className={className + '__statusCard__indicator'}
                                sx={{ backgroundColor: ticketStatusObj?.status?.color }}
                            />
                        </Paper>
                    </Grid>
                ))}
            </Grid>
        </Box>
    );

    if (!currentUserProjectPermissions?.allowView) {
        return (
            <Box className={className + '__restricted'}>
                <ErrorOutlineIcon />
                <Typography>You don't have permission to view analytics</Typography>
            </Box>
        );
    }

    return (
        <div className={className}>
            <Box className={className + '__container'}>
                {renderPrimaryStats()}
                {renderStatusBreakdown()}
            </Box>
        </div>
    );
}
